import React, { useEffect, useState } from "react";
import moment from "moment";
import mainAxios from "../../../redux/api/mainAxios";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { fetchSingleProgram } from "../../../redux/actions/immobilierActions";
import { closeModal } from "../../../redux/actions/modalActions";
import { uploadAndCreateImageEntry } from "../../../utils/createImgforLots";
import { imageLoader } from "../../../utils/imageLoader";

const EditProgramModal = ({ programme }) => {
  const dispatch = useDispatch();
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadedImageEntries, setUploadedImageEntries] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    name: programme?.name || "",
    description: programme?.description || "",
    delivery_date: moment(programme.delivery_date).format("YYYY-MM-DD") || "",
    is_actable: programme?.is_actable || false,
    actibility_date:
      moment(programme.actibility_date).format("YYYY-MM-DD") || "",
  });

  // Store original form data for comparison
  const [originalFormData] = useState({
    name: programme?.name || "",
    description: programme?.description || "",
    delivery_date: moment(programme.delivery_date).format("YYYY-MM-DD") || "",
    is_actable: programme?.is_actable || false,
    actibility_date:
      moment(programme.actibility_date).format("YYYY-MM-DD") || "",
  });

  // Check if form has changes
  const hasFormChanges = () => {
    return Object.keys(formData).some(
      (key) => formData[key] !== originalFormData[key]
    );
  };

  // Check if there are image changes
  const hasImageChanges = () => {
    return selectedImages.length > 0 || imagesToDelete.length > 0;
  };

  const isFormValid = hasFormChanges() || hasImageChanges();

  useEffect(() => {
    if (programme?.images && Array.isArray(programme.images)) {
      const allImages = programme.images.map((img) => ({
        id: img.id,
        url: imageLoader(img.image[0].url),
        name: img.name || img.alternativeText || "Image",
      }));
      setExistingImages(allImages);
    }
  }, [programme]);

  const handleDeleteExistingImage = (imageId) => {
    setShowDeleteConfirm(true);
    setImagesToDelete((prev) => [...prev, imageId]);
  };

  const confirmDelete = async () => {
    if (imagesToDelete.length === 0) return;

    try {
      const imageIdToDelete = imagesToDelete[0];
      const updatedImages = existingImages.filter(
        (img) => img.id !== imageIdToDelete
      );
      const remainingImageIds = updatedImages.map((img) => img.id);

      await mainAxios.put(
        `${process.env.REACT_APP_ENDPOINT_URL}api/programmes/${programme.documentId}`,
        {
          data: {
            images: remainingImageIds,
          },
        }
      );

      setExistingImages(updatedImages);
      dispatch(fetchSingleProgram(programme.documentId));
      toast.success("Image supprimée du programme avec succès");
    } catch (error) {
      console.error("Error unlinking image from programme:", error);
      toast.error("Erreur lors de la suppression de l'image du programme");
    } finally {
      setShowDeleteConfirm(false);
      setImagesToDelete([]);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setImagesToDelete([]);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageUpload = async () => {
    if (selectedImages.length === 0) return [];

    const uploadedEntries = [];

    try {
      for (const file of selectedImages) {
        const reference = programme.name || `programme-${programme.documentId}`;
        const entry = await uploadAndCreateImageEntry(file, reference);
        if (entry) {
          uploadedEntries.push(entry);
        }
      }
      setUploadedImageEntries(uploadedEntries);

      const newImages = uploadedEntries.map((entry) => ({
        id: entry.id,
        url: imageLoader(entry.origine_url),
        name: entry.name || entry.alternativeText || "Image",
      }));

      setExistingImages((prev) => [...prev, ...newImages]);

      toast.success(
        `${uploadedEntries.length} image(s) téléchargée(s) avec succès`
      );

      setSelectedImages([]);

      return uploadedEntries;
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Erreur lors du téléchargement des images");
      return [];
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
  };

  const handleEditProgram = async () => {
    try {
      let imageEntries = uploadedImageEntries;
      if (selectedImages.length > 0 && uploadedImageEntries.length === 0) {
        imageEntries = await handleImageUpload();
      }

      const filteredData = Object.keys(formData).reduce((acc, key) => {
        if (
          formData[key] !== "" &&
          formData[key] !== null &&
          formData[key] !== undefined
        ) {
          acc[key] = formData[key];
        }
        return acc;
      }, {});

      const currentImageIds = existingImages.map((img) => img.id);
      const newImageIds = imageEntries.map((entry) => entry.id);
      const allImageIds = [...currentImageIds, ...newImageIds];

      if (allImageIds.length > 0) {
        filteredData.images = allImageIds;
      }

      const response = await mainAxios.put(
        `${process.env.REACT_APP_ENDPOINT_URL}api/programmes/${programme.documentId}`,
        {
          data: { ...filteredData, isUpdated: true },
        }
      );

      toast.success("Programme mis à jour avec succès");
      dispatch(fetchSingleProgram(programme.documentId));
      dispatch(closeModal());
    } catch (error) {
      console.error("error", error);
      toast.error("Echec de la mise à jour du programme");
    }
  };
  return (
    <div className="w100 d-flex flex-column">
      <div>
        <p className="semibold pink fs16">Fiche du Programme</p>
        <hr />
      </div>
      <div className="d-flex gap40">
        {/* Left Column */}
        <div className="w50 d-flex flex-column gap20">
          <div>
            <label htmlFor="name">Nom</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w100 customInput"
            />
          </div>
          <div>
            <label htmlFor="description">Description</label>
            <textarea
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w100 customInput"
            />
          </div>
          <div>
            <label htmlFor="delivery_date">Date de livraison</label>
            <input
              type="date"
              id="delivery_date"
              name="delivery_date"
              value={formData.delivery_date}
              onChange={handleChange}
              className="w100 customInput"
            />
          </div>
          <div>
            <label htmlFor="is_actable">Actable</label>
            <input
              type="checkbox"
              id="is_actable"
              name="is_actable"
              checked={formData.is_actable}
              onChange={handleChange}
              className="w100 customInput"
            />
          </div>
          <div>
            <label htmlFor="actibility_date">Date d'actibilité</label>
            <input
              type="date"
              id="actibility_date"
              name="actibility_date"
              value={formData.actibility_date}
              onChange={handleChange}
              className="w100 customInput"
            />
          </div>
        </div>
        {/* Image Upload Section */}
        <div className="w50 d-flex flex-column gap20">
          <div>
            <label>Ajouter des images</label>
            <button
              type="button"
              className="radius5 px-20 py-10 bg-pink text-white border-0 hoverOpacity"
              onClick={() =>
                document.getElementById("image-input-edit").click()
              }
            >
              Sélectionner des images
            </button>
            <input
              id="image-input-edit"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="d-none"
            />
          </div>
          {selectedImages.length > 0 && (
            <div className="selected-images d-flex flex-column gap10 mt-2">
              <p className="fw-bold mb-2">
                Images sélectionnées ({selectedImages.length}):
              </p>
              <div className="image-previews d-flex flex-wrap gap10">
                {selectedImages.map((file, index) => (
                  <div
                    key={index}
                    className="image-preview-item position-relative p-2 border rounded bg-light"
                  >
                    <p className="lot-image-name mb-0">{file.name}</p>
                    <button
                      type="button"
                      className="remove-image-btn position-absolute top-0 end-0 btn btn-sm text-danger"
                      onClick={() => removeImage(index)}
                      aria-label="Supprimer cette image"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {existingImages.length > 0 && (
            <div>
              <p>Images existantes:</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {existingImages.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "5px",
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.name}
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                    <button
                      onClick={() => handleDeleteExistingImage(image.id)}
                      style={{
                        position: "absolute",
                        top: "2px",
                        right: "2px",
                        background: "red",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              minWidth: "300px",
              textAlign: "center",
            }}
          >
            <div style={{ marginBottom: "20px", fontSize: "16px" }}>
              Êtes-vous sûr de vouloir supprimer cette image du programme ?
            </div>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={confirmDelete}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Oui, supprimer
              </button>
              <button
                onClick={cancelDelete}
                style={{
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="d-flex justify-content-end mt-20">
        <button
          onClick={handleEditProgram}
          disabled={!isFormValid}
          className={`radius5 px-20 py-1 white border-0 hoverOpacity ${
            isFormValid ? "bg-pink" : "bg-soft-grey"
          }`}
        >
          Appliquer
        </button>
      </div>
    </div>
  );
};

export default EditProgramModal;
